import * as React from 'react'
import styled from 'styled-components'
import { Link } from 'gatsby'
import { Router, RouteComponentProps } from '@reach/router'
import { useTransition } from '@react-spring/web'
import { MainNavigation } from 'src/components/layout/Navigation'
import { SidebarNavigation } from 'src/components/layout/Navigation/Sidebar'
import { RouteState } from 'src/constants/routes'
import { observer } from 'mobx-react-lite'
import { isLoggedIn } from 'src/api/operations/auth'
import {
  LoginIcon,
  ProjectsIcon,
  CubeIcon
} from 'src/components/icons/navigationIcons'
import { BoxtronikIcon } from 'src/components/icons/BoxtronikIcon'

const StyledAppBar = styled.header`
  width: 100%;
  height: 64px;
  position: fixed;
  z-index: 5000;
  background-color: #659fa2;
  display: flex;
  justify-content: stretch;
`

const Title = styled.h2.attrs(({ title, children = title, ...props }) => ({
  ...props,
  children,
  title
}))`
  color: #90bca4;
  font-size: 24px;
  text-transform: uppercase;
  margin: 0;
  line-height: 64px;
  padding: 0 24px;
  box-sizing: border-box;
  text-decoration: none;
`

const IconWrapper = styled.figure`
  width: 24px;
  height: 24px;
  margin: 0;
  padding: 12px 20px 6px 20px;
`

const IconTitle = styled.h3`
  margin: 0;
  padding: 0;
  text-transform: uppercase;
  line-height: 9px;
  font-size: 9px;
  font-weight: 300;
  color: var(--color);
`

const IconLink = styled(Link)`
  --color: rgb(255, 255, 255);
  display: block;
  text-decoration: none;
  text-align: center;
  height: 64px;
  width: 64px;
  outline: none;
  &.active {
    --color: #346b6e;
  }
`

interface IconButtonProps {
  to: string
  reeplace?: boolean
  title: string
  Icon?: React.ComponentType
}

const IconButton: React.FC<IconButtonProps> = ({
  replace,
  to,
  title,
  Icon
}) => {
  return (
    <IconLink {...{ to, replace }}>
      <IconWrapper>
        {Icon && <Icon {...{ svgProps: { width: 24, height: 24 } }} />}
      </IconWrapper>
      <IconTitle>{title}</IconTitle>
    </IconLink>
  )
}

const Sidebar = styled(StyledAppBar)`
  width: 320px;
  position: relative;
  background: #346b6e;
`

const MenuWrapper = styled.nav`
  padding: 0 8px;
  box-sizing: border-box;
  display: flex;
  line-height: 64px;
`

const MenuItem = styled.span`
  padding: 0 8px;
  box-sizing: border-box;
  color: rgb(255, 255, 255);
  font-weight: 300;
`

const menuItems = [{ title: 'Project' }, { title: 'Edit' }]
const projectsManagmentItems = [
  ...menuItems,
  { title: 'Account' },
  { title: 'Help' }
]
const setupItems = [
  ...menuItems,
  { title: 'View' },
  { title: 'Account' },
  { title: 'Help' }
]
const editorItems = [
  ...menuItems,
  { title: 'Object' },
  { title: 'Configuration' },
  { title: 'View' },
  { title: 'Account' },
  { title: 'Help' }
]

type MenuProps = {
  items?: { title: string }[]
} & RouteComponentProps

const Menu: React.FC<MenuProps> = ({ items = menuItems }) => {
  return (
    <MenuWrapper>
      {items.map((item, key) => (
        <MenuItem key={key}>{item.title}</MenuItem>
      ))}
    </MenuWrapper>
  )
}

type NavRouteProps = {
  path: string
  children: React.ReactElement
}
const NavRoute: React.FC<NavRouteProps> = ({ path, children }) => {
  return (
    <Router>
      <children.type {...{ ...children.props, path }} />
    </Router>
  )
}

/**
 *
 */

const IconNavigationWrapper = styled.nav`
  display: flex;
  padding-right: 4px;
`

const IconNavigation = () => (
  <IconNavigationWrapper>
    {isLoggedIn() ? (
      <>
        <IconButton
          to={'/'}
          title="Boxtronik"
          Icon={BoxtronikIcon}
        ></IconButton>
        <IconButton to={'/projects'} title="Projects" Icon={ProjectsIcon} />
      </>
    ) : (
      <IconButton {...{ to: '/login', title: 'Login', Icon: LoginIcon }} />
    )}
  </IconNavigationWrapper>
)

const RightPartWrapper = styled.div`
  display: flex;
`

const LeftSide = () => {
  return (
    <Router {...{ component: MenuWrapper }}>
      <MenuItem path="/project/:projectID">Project</MenuItem>
    </Router>
  )
}

const FlexWrapper = styled.nav`
  display: flex;
  flex: 1;
  justify-content: space-between;
`

type AppBarProps = {
  routeState: RouteState
  ['*']?: string
  projectID: string
}

export const AppBar: React.FC<AppBarProps> = observer(
  ({ '*': state, projectID, routeState, ...props }) => {
    return (
      <StyledAppBar>
        <FlexWrapper>
          <MainNavigation {...{ routeState, state, projectID }} />
          <Title {...{ title: 'BOXTRONIK' }} />
          <Router>
            <IconNavigation path="/*" />
          </Router>
        </FlexWrapper>
        {routeState.page === 'project' && routeState.params?.projectID && (
          <SidebarNavigation {...{ routeState }} />
        )}
      </StyledAppBar>
    )
  }
)
